import Image from '@base/Image';
import { forwardRef } from 'react';
import styled from '@emotion/styled';
import pushRoute from '@utils/pushRoute';
import Paper from 'aquilla/core/atoms/Paper';
import { emptyObject } from '@constants/empty';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { unlockFreePlanFor } from '@constants/index';
import ClassIcon from 'aquilla/icons/features/Class';
import { Spacings, mediaQuery } from '@styles/index';
import useTestSeriesStore from '@stores/test-series';
import Typography from 'aquilla/core/atoms/Typography';
import { convertMinsToHrsMins, getTime } from '@utils/dates';
import { getIsLoggedIn, getIsPlatformUnlocked } from '@api/selectors/auth';
import {
  CARD_TYPE,
  CARD_STATE,
  CARD_WIDTH,
  MODAL_TYPE,
  classIconColor,
  UPCOMING_CLASS_ENABLE_THRESHOLD
} from './constants';
import useHandleSaveItem from './useHandleSaveItem';
import {
  getCardState,
  getIsCardDisabled,
  getSecondsDiff,
  useIsFromStore
} from './utils';

const Wrapper = styled(Paper)`
  display: flex;
  position: relative;
  width: ${CARD_WIDTH};
  padding: ${Spacings.SPACING_6B};
  min-height: ${Spacings.SPACING_22B};
  margin-bottom: ${Spacings.SPACING_5B};
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  cursor: ${(props) => (props.$disableCardClick ? 'initial' : 'pointer')};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    min-height: ${Spacings.SPACING_18B};
  }
`;

const InfoWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: ${Spacings.SPACING_4B};
  margin-right: ${Spacings.SPACING_8B};
`;

const PostInfo = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_2};
  color: var(--color-text-secondary);
`;

const LivePill = styled.div`
  display: inline-flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_6};
`;

const SaveWrapper = styled.div`
  position: absolute;
  top: 25px;
  right: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: ${Spacings.SPACING_10B};
`;

const getCardIcon = (state) => {
  if (state === CARD_STATE.LIVE) {
    return (
      <IconWrapper>
        <Image
          src="store/test-series/live-class.svg"
          height={40}
          width={40}
          alt="live-class"
        />
      </IconWrapper>
    );
  }
  if (state === CARD_STATE.UPCOMING) {
    return (
      <IconWrapper>
        <Image
          src="store/test-series/class-icon.svg"
          height={40}
          width={40}
          alt="live-class"
        />
      </IconWrapper>
    );
  }
  return <ClassIcon height={40} width={40} color={classIconColor} />;
};

const PostCard = forwardRef(
  (
    {
      postInfo,
      className,
      hasSkuAccess,
      isSubscribed,
      showSaveIcon,
      collectionUid
    },
    ref
  ) => {
    const fromStore = useIsFromStore();
    const isLoggedIn = useMyInfo(getIsLoggedIn);
    const { setModalInfo } = useTestSeriesStore();
    const isPlatformUnlocked = useMyInfo(getIsPlatformUnlocked);

    const {
      isLive,
      goalUid,
      isSaved: initialSaveState,
      value: { duration, startTime, title, slug = '-', uid } = emptyObject
    } = postInfo ?? emptyObject;

    const { isSaved, handleSaveClick } = useHandleSaveItem({
      uid,
      title,
      goalUid,
      initialSaveState,
      type: CARD_TYPE.POST
    });

    const state = getCardState(isLive, startTime);

    const getInfoString = () => {
      if (state === CARD_STATE.POST_LIVE) {
        return convertMinsToHrsMins(duration, false, true);
      }
      if (state === CARD_STATE.LIVE) {
        return `Class started at ${getTime(startTime)}`;
      }

      return getTime(startTime);
    };

    const isUpcomingClassDisabled =
      state === CARD_STATE.UPCOMING &&
      getSecondsDiff(startTime) > UPCOMING_CLASS_ENABLE_THRESHOLD;

    const isCardClickDisabled =
      getIsCardDisabled({
        isLoggedIn,
        fromStore,
        hasSkuAccess,
        isSubscribed
      }) || isUpcomingClassDisabled;

    const handleCardClick = () => {
      if (isCardClickDisabled) {
        return;
      }

      if (isLoggedIn && !isPlatformUnlocked) {
        return setModalInfo({
          objectUid: collectionUid,
          type: MODAL_TYPE.FREE_PLAN_UNLOCK,
          objectType: unlockFreePlanFor.COURSE,
          callBack: () => pushRoute(`/class/${slug}/${uid}`)
        });
      }

      pushRoute(`/class/${slug}/${uid}`);
    };

    const onSaveClick = (e) => {
      e.stopPropagation();
      handleSaveClick();
    };

    return (
      <Wrapper
        ref={ref}
        className={className}
        onClick={handleCardClick}
        $disableCardClick={isCardClickDisabled}
      >
        {getCardIcon(state)}
        <InfoWrapper>
          <div>
            <Typography variant="h6">{title}</Typography>
            <PostInfo variant="p3">
              {state === CARD_STATE.LIVE ? (
                <LivePill>
                  <Image
                    src="store/test-series/live-pill.svg"
                    width={40}
                    height={20}
                    alt="live"
                  />
                </LivePill>
              ) : null}
              {getInfoString()}
            </PostInfo>
          </div>
        </InfoWrapper>
        {showSaveIcon ? (
          <SaveWrapper>
            <Image
              src={
                isSaved
                  ? 'store/test-series/icon-unsave.svg'
                  : 'store/test-series/icon-save.svg'
              }
              width={12}
              height={16}
              alt="save"
              onClick={onSaveClick}
            />
          </SaveWrapper>
        ) : null}
      </Wrapper>
    );
  }
);

PostCard.displayName = 'PostCard';

export default PostCard;
